import { useState, useCallback, useRef, useEffect } from 'react'
import type { FDEConversation, FDEMessage, OrganizationBilling } from '@/types/fde'

interface ChannelEvent {
  type: string
  data: any
  timestamp: string
}

interface UseFDEConversationStateOptions {
  initialConversation: FDEConversation
  initialMessages: FDEMessage[]
  initialProcessing: boolean
  organization: OrganizationBilling
}

export function useFDEConversationState({
  initialConversation,
  initialMessages,
  initialProcessing,
  organization: initialOrg,
}: UseFDEConversationStateOptions) {
  const [conversation, setConversation] = useState(initialConversation)
  const [messages, setMessages] = useState<FDEMessage[]>(initialMessages)
  const [processing, setProcessing] = useState(initialProcessing)
  const [currentActivity, setCurrentActivity] = useState<string | null>(null)
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [organization, setOrganization] = useState(initialOrg)

  const pendingChunksRef = useRef<Map<number, string>>(new Map())
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync initial props when Inertia navigates
  useEffect(() => {
    setConversation(initialConversation)
    setMessages(initialMessages)
    setProcessing(initialProcessing)
    setOrganization(initialOrg)
  }, [initialConversation.id])

  // Cleanup flush timer on unmount
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current)
      }
    }
  }, [])

  // Flush pending chunks to state
  const flushChunks = useCallback(() => {
    pendingChunksRef.current.forEach((content, messageId) => {
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === messageId)
        if (idx === -1) {
          // New streaming message
          return [...prev, {
            id: messageId,
            fde_conversation_id: conversation.id,
            content,
            role: 'assistant' as const,
            status: 'processing' as const,
            user: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }]
        }
        const updated = [...prev]
        updated[idx] = { ...updated[idx], content }
        return updated
      })
    })
    pendingChunksRef.current.clear()
  }, [conversation.id])

  // Handle ActionCable events
  const handleChannelEvent = useCallback((event: ChannelEvent) => {
    switch (event.type) {
      case 'message_chunk': {
        const { message_id, content } = event.data
        pendingChunksRef.current.set(message_id, content)

        if (flushTimerRef.current) clearTimeout(flushTimerRef.current)
        flushTimerRef.current = setTimeout(flushChunks, 30)
        break
      }

      case 'message_complete': {
        // Clear any pending chunks for this message
        pendingChunksRef.current.delete(event.data.id)

        setMessages(prev => {
          const idx = prev.findIndex(m => m.id === event.data.id)
          if (idx === -1) return [...prev, event.data]
          const updated = [...prev]
          updated[idx] = event.data
          return updated
        })
        setCurrentActivity(null)
        break
      }

      case 'processing_status': {
        setProcessing(event.data.processing)
        if (!event.data.processing) setCurrentActivity(null)
        break
      }

      case 'credits_update': {
        setOrganization(prev => ({
          ...prev,
          credits_remaining: event.data.credits_remaining,
          credits_total: event.data.credits_total,
        }))
        break
      }
    }
  }, [flushChunks])

  // Send a message
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isSendingMessage || processing) return

    setIsSendingMessage(true)

    // Optimistic user message
    const tempId = -Date.now()
    const optimisticMsg: FDEMessage = {
      id: tempId,
      fde_conversation_id: conversation.id,
      content,
      role: 'user',
      status: 'completed',
      user: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimisticMsg])
    setProcessing(true)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch(`/fde/${conversation.id}/fde_messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify({ message: { content } }),
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        // Remove optimistic message on error
        setMessages(prev => prev.filter(m => m.id !== tempId))
        setProcessing(false)
        throw new Error(data.error || data.errors?.join(', ') || 'Failed to send')
      }
    } catch (err) {
      setMessages(prev => prev.filter(m => m.id !== tempId))
      setProcessing(false)
      throw err
    } finally {
      setIsSendingMessage(false)
    }
  }, [conversation.id, isSendingMessage, processing])

  return {
    conversation,
    messages,
    processing,
    currentActivity,
    isSendingMessage,
    organization,
    handleChannelEvent,
    sendMessage,
  }
}
